import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { productService } from '../services/productService';
import { uploadService } from '../services/uploadService';
import { LOCAL_CATEGORIES } from '../constants/localCategories';
import { formatPrice } from '../utils/priceHelpers';

const emptyForm = {
  name: '',
  description: '',
  category: 'autre',
  price: '',
  stock: '',
  thumbnailUrl: '',
  isActive: true,
  isFeatured: false,
};

const AdminProducts = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (user === null) return;
    if (!user || !['admin', 'agent'].includes(user.role)) {
      navigate('/dashboard');
      return;
    }
    loadProducts();
  }, [user, navigate]);

  const loadProducts = async () => {
    setLoading(true);
    const res = await productService.getAllProducts();
    if (res.success) setProducts(res.products);
    setLoading(false);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || '',
      description: p.description || '',
      category: p.category || 'autre',
      price: String(p.price ?? ''),
      stock: String(p.stock ?? ''),
      thumbnailUrl: p.thumbnailUrl || p.images?.[0] || '',
      isActive: p.isActive !== false,
      isFeatured: Boolean(p.isFeatured),
    });
    setShowForm(true);
  };

  const handleImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const res = await uploadService.uploadImage(file);
    setUploading(false);
    if (res.success) {
      setForm((f) => ({ ...f, thumbnailUrl: res.imageUrl }));
      toast.success('Image uploadée');
    } else {
      toast.error(res.error || 'Erreur upload');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price: Number(form.price),
      stock: Number(form.stock),
      images: form.thumbnailUrl ? [form.thumbnailUrl] : [],
    };
    const res = editing
      ? await productService.updateProduct(editing.id, payload)
      : await productService.createProduct(payload);
    if (res.success) {
      toast.success(editing ? 'Produit mis à jour' : 'Produit publié');
      setShowForm(false);
      loadProducts();
    } else {
      toast.error(res.error || 'Erreur');
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Désactiver ce produit ?')) return;
    const res = await productService.deactivateProduct(id);
    if (res.success) {
      toast.success('Produit désactivé');
      loadProducts();
    }
  };

  return (
    <div>
      <Navbar />
      <div className="container" style={{ padding: '30px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <h1>Catalogue boutique locale</h1>
            <p style={{ color: '#666' }}>Publier et gérer les produits vendus en RDC.</p>
          </div>
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            + Nouveau produit
          </button>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="card" style={{ padding: 24, marginBottom: 24 }}>
            <h2>{editing ? 'Modifier' : 'Publier'} un produit</h2>
            <div className="form-group">
              <label>Nom *</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Catégorie</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {LOCAL_CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="form-group">
                <label>Prix (USD) *</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  required
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Stock *</label>
                <input
                  type="number"
                  min="0"
                  required
                  value={form.stock}
                  onChange={(e) => setForm({ ...form, stock: e.target.value })}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Image</label>
              <input type="file" accept="image/*" onChange={handleImage} disabled={uploading} />
              {form.thumbnailUrl && (
                <img src={form.thumbnailUrl} alt="" style={{ maxWidth: 120, marginTop: 8, borderRadius: 8 }} />
              )}
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              Visible en boutique
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <input
                type="checkbox"
                checked={form.isFeatured}
                onChange={(e) => setForm({ ...form, isFeatured: e.target.checked })}
              />
              Mettre en vedette (accueil)
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="submit" className="btn btn-primary">
                Enregistrer
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                Annuler
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <p>Chargement…</p>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {products.map((p) => (
              <div
                key={p.id}
                className="card"
                style={{ display: 'flex', gap: 16, padding: 16, alignItems: 'center' }}
              >
                {p.thumbnailUrl ? (
                  <img src={p.thumbnailUrl} alt="" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8 }} />
                ) : (
                  <div style={{ width: 64, height: 64, background: '#eee', borderRadius: 8 }} />
                )}
                <div style={{ flex: 1 }}>
                  <strong>{p.name}</strong>
                  <p style={{ margin: 4, color: '#666', fontSize: 14 }}>
                    {formatPrice(p.price)} — Stock {p.stock}
                    {!p.isActive && ' — Inactif'}
                    {p.isFeatured && ' — ★ Vedette'}
                  </p>
                </div>
                <button type="button" className="btn btn-secondary" onClick={() => openEdit(p)}>
                  Modifier
                </button>
                {p.isActive && (
                  <button type="button" className="btn btn-secondary" onClick={() => handleDeactivate(p.id)}>
                    Désactiver
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default AdminProducts;
